/**
 * Client for the backend Routy agent. Uses the same authenticated request layer
 * as the generated API client (`request(OpenAPI, ...)`), so it inherits the base
 * URL and bearer token wired up at app bootstrap.
 *
 * This is the pattern used elsewhere for endpoints that aren't in the generated
 * client yet (see `fetchSlotsWithPreferredUser` in CreateBookingModal). Once the
 * backend's OpenAPI exposes these endpoints and `npm run generate-api` is run,
 * swap the bodies to call the generated `RoutyAssistantService`.
 */
import { OpenAPI } from '../../api/core/OpenAPI';
import { request as apiRequest } from '../../api/core/request';
import type {
  RoutyTurnResponse,
  RoutyExecuteRequest,
  RoutyExecuteResponse,
  RoutyConversation,
  RoutyConversationSummary,
} from './assistantContract';

const ERRORS = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
  501: 'Not Implemented',
} as const;

export function newIdempotencyKey(): string {
  // crypto.randomUUID is available in all supported browsers; fall back just in case.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const RoutyAssistantClient = {
  sendMessage(text: string, conversationId?: string): Promise<RoutyTurnResponse> {
    return apiRequest<RoutyTurnResponse>(OpenAPI, {
      method: 'POST',
      url: '/api/app/routy-assistant/message',
      body: { conversationId, text },
      mediaType: 'application/json',
      errors: ERRORS,
    });
  },

  execute(req: RoutyExecuteRequest): Promise<RoutyExecuteResponse> {
    return apiRequest<RoutyExecuteResponse>(OpenAPI, {
      method: 'POST',
      url: '/api/app/routy-assistant/execute',
      body: req,
      mediaType: 'application/json',
      errors: ERRORS,
    });
  },

  getConversation(id: string): Promise<RoutyConversation> {
    return apiRequest<RoutyConversation>(OpenAPI, {
      method: 'GET',
      url: '/api/app/routy-assistant/conversation/{id}',
      path: { id },
      errors: ERRORS,
    });
  },

  listConversations(): Promise<RoutyConversationSummary[]> {
    return apiRequest<RoutyConversationSummary[]>(OpenAPI, {
      method: 'GET',
      url: '/api/app/routy-assistant/conversations',
      errors: ERRORS,
    });
  },

  newConversation(): Promise<{ conversationId: string }> {
    return apiRequest<{ conversationId: string }>(OpenAPI, {
      method: 'POST',
      url: '/api/app/routy-assistant/conversation',
      errors: ERRORS,
    });
  },

  clearConversation(id: string): Promise<void> {
    return apiRequest<void>(OpenAPI, {
      method: 'DELETE',
      url: '/api/app/routy-assistant/conversation/{id}',
      path: { id },
      errors: ERRORS,
    });
  },
};
